import { Injectable } from '@angular/core';
import {
  SignInInput,
  SignUpOutput,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updateUserAttributes,
} from '@aws-amplify/auth';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import { plainToInstance } from 'class-transformer';
import { Observable, catchError, from, map, of, switchMap } from 'rxjs';
import { User } from 'src/app/shared/models/user.model';
import { AuthProvider } from 'src/shared/enum/auth-provider';
import { UserType } from '@shared/types/user-type';

@Injectable({
  providedIn: 'root',
})
export class AmplifyConfigurationService {
  configure(config: ResourcesConfig) {
    Amplify.configure(config);
  }

  async registerUser(user: User): Promise<any> {
    const { username, password, email, firstName, lastName, birthday, userType } = user;
    try {
      const userAttributes: any = {
        email,
        given_name: firstName,
        family_name: lastName,
        'custom:userType': userType,
        'custom:isPaidUser': 'false',
      };

      if (birthday) {
        userAttributes.birthdate = birthday.toISOString().split('T')[0];
      }

      const result: SignUpOutput = await signUp({
        username,
        password,
        options: {
          userAttributes: userAttributes,
        },
      });

      return result;
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  }

  async handleConfirmSignUp(username: string, confirmationCode: string) {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username: username,
        confirmationCode: confirmationCode,
      });

      if (isSignUpComplete) {
      } else {
      }
    } catch (error) {
      console.error('Error confirming sign up:', error);
    }
  }

  async signIn(username: string, password: string): Promise<any> {
    // Rely on Amplify's default USER_SRP_AUTH for maximum security
    return await signIn({ username, password });
  }

  async signOut() {
    try {
      await signOut({ global: true });
    } catch (error) {
      console.warn(
        'AmplifyConfigService: Error during global sign out, falling back to local',
        error,
      );
      try {
        await signOut();
      } catch (e) {
        console.warn('AmplifyConfigService: Error during local sign out', e);
      }
    }
  }

  async signInWithGoogle() {
    try {
      await signInWithRedirect({ provider: AuthProvider.Google });
    } catch (error) {
      console.error('AmplifyConfigService: Error signing in with Google:', error);
      throw error; // Let the UI know it failed
    }
  }

  async userAuthentication(): Promise<any> {
    return await fetchAuthSession();
  }

  /**
   * Forces Cognito to issue fresh tokens so that any recently updated
   * custom attributes (e.g. custom:userType set after a Google OAuth redirect)
   * are immediately reflected in the next fetchAuthSession() call.
   *
   * Call this after updateUserAttributes() to avoid reading stale cached tokens.
   */
  async forceRefreshSession(): Promise<void> {
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch (error) {
      // Non-fatal: log and continue. A failed force-refresh does not block
      // the user — the token will be refreshed on the next natural expiry.
      console.warn('AmplifyConfigService: forceRefreshSession failed:', error);
    }
  }

  async updateAttributes(attributes: any): Promise<any> {
    try {
      const result = await updateUserAttributes({
        userAttributes: attributes,
      });
      return result;
    } catch (error) {
      console.error('Error updating user attributes:', error);
      throw error;
    }
  }

  isUserLogedIn(): Observable<User | null> {
    return from(getCurrentUser()).pipe(
      switchMap(() => from(fetchAuthSession())),
      map((session: any) => {
        if (!session.tokens) {
          return null;
        }

        const sessionData = session as any;
        if (!sessionData.tokens) return null;

        const { idToken, signInDetails } = sessionData.tokens;
        const { email, given_name, family_name, address } = idToken?.payload || {};

        return plainToInstance(User, {
          username: idToken?.payload?.['cognito:username'] || signInDetails?.loginId || 'User',
          email: email || idToken?.payload?.email,
          firstName: given_name,
          lastName: family_name,
          currentLocation: address?.formatted || '',
          userType: idToken?.payload?.['custom:userType'] || UserType.CONSUMER,
          isPaidUser: idToken?.payload?.['custom:isPaidUser'] === 'true',
          reviewCount: Number(idToken?.payload?.['custom:reviewCount'] || 0),
          eventCount: Number(idToken?.payload?.['custom:eventCount'] || 0),
        });
      }),
      catchError((err) => {
        return of(null);
      }),
    );
  }
}
